import React, { useState, useEffect } from 'react';
import styles from './Comunicados.module.css';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import {
  User, Calendar, Eye, ChevronLeft, ChevronRight, MoreVertical, Trash2, MapPin,
  File,
  Plus,
  Edit
} from 'lucide-react';
import { API_URL } from '../../api';
import { mascaraCNPJ } from '../../utils/mascaras';
import { useNavigate } from 'react-router-dom';

const statusColors = {
  'pendente': { bg: '#fef9c3', color: '#b45309' },
  'processando': { bg: '#dbeafe', color: '#2563eb' },
  'cancelado': { bg: '#fee2e2', color: '#e11d48' },
  'enviado': { bg: '#ede9fe', color: '#7c3aed' },
};

function formatDate(dataStr, horaStr = '') {
  if (!dataStr) return '';
  
  const data = new Date(dataStr);
  const dataFormatada = data.toLocaleDateString('pt-BR');
  
  if (horaStr) {
    return `${dataFormatada}, ${horaStr}`;
  }
  
  return dataFormatada;
}

function ListaComunicados() {
  const [menuAberto, setMenuAberto] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);
  const [filtros, setFiltros] = useState({
    nomeCliente: '',
    numeroCliente: '',
    cnpjCliente: '',
    dataInicial: '',
    dataFinal: '',
    status: '',
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState({});
  const [comunicados, setComunicados] = useState([]);
  const [totalItens, setTotalItens] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [menuAcoesAberto, setMenuAcoesAberto] = useState(null);
  const [clienteDetalhes, setClienteDetalhes] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    async function fetchComunicados() {
      setCarregando(true);
      try {
        let url = `${API_URL}/comunicados?page=${paginaAtual}`;
        // Adicionar filtros aplicados na query string
        const params = [];
        if (filtrosAplicados.nomeCliente) params.push(`nome_cliente=${encodeURIComponent(filtrosAplicados.nomeCliente)}`);
        if (filtrosAplicados.numeroCliente) params.push(`numero_cliente=${encodeURIComponent(filtrosAplicados.numeroCliente)}`);
        if (filtrosAplicados.cnpjCliente) params.push(`cnpj_cliente=${encodeURIComponent(filtrosAplicados.cnpjCliente)}`);
        if (filtrosAplicados.dataInicial) params.push(`data_inicial=${encodeURIComponent(filtrosAplicados.dataInicial)}`);
        if (filtrosAplicados.dataFinal) params.push(`data_final=${encodeURIComponent(filtrosAplicados.dataFinal)}`);
        if (filtrosAplicados.status) params.push(`status=${encodeURIComponent(filtrosAplicados.status)}`);
        if (params.length > 0) {
          url += '&' + params.join('&');
        }
        const token = localStorage.getItem('token');
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        setComunicados(data.data || []);
        setTotalItens(data.total || 0);
        setTotalPaginas(data.last_page || 1);
      } catch (e) {
        setComunicados([]);
        setTotalItens(0);
        setTotalPaginas(1);
      } finally {
        setCarregando(false);
      }
    }
    fetchComunicados(); 
  }, [paginaAtual, filtrosAplicados]);

  const handleChangeFiltro = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const handlePesquisar = (e) => {
    e.preventDefault();
    setFiltrosAplicados({ ...filtros });
    setPaginaAtual(1);
  };

  const handleLimpar = () => {
    const filtrosLimpos = {
      nomeCliente: '',
      numeroCliente: '',
      cnpjCliente: '',
      dataInicial: '',
      dataFinal: '',
      status: '',
    };
    setFiltros(filtrosLimpos);
    setFiltrosAplicados(filtrosLimpos);
    setPaginaAtual(1);
  };

  const mudarPagina = (novaPagina) => {
    if (novaPagina >= 1 && novaPagina <= totalPaginas) {
      setPaginaAtual(novaPagina);
    }
  };

  // Gerar array de páginas para exibir
  const gerarPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    if (totalPaginas <= maxPaginasVisiveis) {
      for (let i = 1; i <= totalPaginas; i++) {
        paginas.push(i);
      }
    } else {
      if (paginaAtual <= 3) {
        for (let i = 1; i <= 4; i++) {
          paginas.push(i);
        }
        paginas.push('...');
        paginas.push(totalPaginas);
      } else if (paginaAtual >= totalPaginas - 2) {
        paginas.push(1);
        paginas.push('...');
        for (let i = totalPaginas - 3; i <= totalPaginas; i++) {
          paginas.push(i);
        }
      } else {
        paginas.push(1);
        paginas.push('...');
        for (let i = paginaAtual - 1; i <= paginaAtual + 1; i++) {
          paginas.push(i);
        }
        paginas.push('...');
        paginas.push(totalPaginas);
      }
    }
    return paginas;
  };

  const handleAbrirMenuAcoes = (clienteId) => {
    setMenuAcoesAberto(clienteId);
  };
  const handleFecharMenuAcoes = () => {
    setMenuAcoesAberto(null);
  };

  // Função para redirecionar para a página de editar
  const handleEditar = (comunicado) => {
    navigate(`/comunicado/editar/${comunicado.id}`);
    setMenuAcoesAberto(null);
  };

  return (
    <div className="dashboard-root">
      <Sidebar menuAberto={menuAberto} setMenuAberto={setMenuAberto} />
      <div className="main-content">
        <Topbar
          setMenuAberto={setMenuAberto}
          titulo="Comunicados"
          botao={<button className="btn-novo-comunicado">+ Novo Comunicado</button>}
        />
        <div className="conteudo-dashboard comunicados-listagem">
          <div className="card-page-main">
            <div className="card-page-main-header">
              <div className="card-page-main-header-title">
                <h2>Comunicados</h2>
              </div>
              <button className="btn btn-primary">
                  <Plus size={18} style={{ marginRight: 6 }} /> Novo Comunicado
                </button>
            </div>
            {/* Filtros de Pesquisa - Collapse */}
            {/* <div className="filtros-collapse">
              <div className="filtros-collapse-header" onClick={() => setFiltrosAbertos((v) => !v)}>
                <Filter size={15} style={{ marginRight: 8 }} />
                <span>Filtros de Pesquisa</span>
                <span className="filtros-collapse-arrow">
                  {filtrosAbertos ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </span>
              </div>
              {filtrosAbertos && (
                <form className="filtros-form" onSubmit={handlePesquisar}>
                  <div className="filtros-grid">
                    <div className="filtro-item">
                      <label>Nome do Cliente</label>
                      <input
                        type="text"
                        name="nomeCliente"
                        placeholder="Digite o nome do cliente..."
                        value={filtros.nomeCliente}
                        onChange={handleChangeFiltro}
                      />
                    </div>
                    
                    <div className="filtro-item">
                      <label>CNPJ do Cliente</label>
                      <input
                        type="text"
                        name="cnpjCliente"
                        placeholder="Digite o CNPJ..."
                        value={filtros.cnpjCliente}
                        onChange={handleChangeFiltro}
                      />
                    </div>
                    
                    <div className="filtro-item">
                      <label>Data de Cadastro</label>
                      <input
                        type="date"
                        name="dataCadastro"
                        placeholder="dd/mm/aaaa"
                        value={filtros.dataCadastro}
                        onChange={handleChangeFiltro}
                      />
                    </div>
                  </div>
                  <div className="filtros-botoes">
                    <button type="submit" className="btn-pesquisar">
                      <Search size={16} style={{ marginRight: 6 }} /> Pesquisar
                    </button>
                    <button type="button" className="btn-limpar" onClick={handleLimpar}>
                      Limpar Filtros
                    </button>
                  </div>
                </form>
              )}
            </div> */}
            
            <div className="cards-list">
              {carregando ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>Carregando comunicados...</div>
              ) : comunicados.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>Nenhum comunicado encontrado.</div>
              ) : (
                comunicados.map(comunicado => (
                  <div className="card-list" key={comunicado.id}>
                    <div className="card-list-header">
                      <span className={`card-titulo ${styles.listCardTitulo}` } >{comunicado.titulo}</span>
                      
                      <div className="card-list-acoes">
                        <button
                          className="card-list-acoes-btn"
                          onClick={() => menuAcoesAberto === comunicado.id ? handleFecharMenuAcoes() : handleAbrirMenuAcoes(comunicado.id)}
                          tabIndex={0}
                          aria-label="Ações do comunicado"
                        >
                          <MoreVertical size={20} />
                        </button>
                        {menuAcoesAberto === comunicado.id && (
                          <div className="card-list-menu-popover" onMouseLeave={handleFecharMenuAcoes}>
                            <button className="card-list-menu-item" onClick={() => handleEditar(comunicado)}> 
                              <Edit size={16} style={{ marginRight: 8 }} /> Editar
                            </button>
                            <button className="card-list-menu-item comunicado-menu-excluir" onClick={() => { handleFecharMenuAcoes(); /* ação excluir */ }}>
                              <Trash2 size={16} style={{ marginRight: 8 }} /> Excluir
                            </button>
                            
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="card-list-body">
                      <div className={`${styles.listCardDescricao}`}>
                          {comunicado.descricao}
                      </div>

                      <div className={`${styles.listCardDocData}`}>
                        <div className="apr-cliente-data">
                            <Calendar size={16} style={{ marginRight: 4 }} />
                            {formatDate(comunicado.data_hora)}
                          </div>
                        {comunicado.documento_anexado ? (
                          <div>
                            <a className={`${styles.listCardDocBtn}`} href={comunicado.documento_anexado} target="_blank" rel="noopener noreferrer">
                              <File size={15} style={{ marginRight: 4 }} />
                              Ver documento
                            </a>
                          </div>
                        ) : null}
                      </div>
                      
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Componente de Paginação */}
            {totalPaginas > 1 && (
              <div className="paginacao-container">
                <div className="paginacao-info">
                  <span>
                    Mostrando {(paginaAtual - 1) * 10 + 1} a {Math.min(paginaAtual * 10, totalItens)} de {totalItens} comunicados
                  </span>
                </div>
                <div className="paginacao-controles">
                  <button
                    className="paginacao-btn"
                    onClick={() => mudarPagina(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                  >
                    <ChevronLeft size={16} />
                    Anterior
                  </button>
                  
                  <div className="paginacao-numeros">
                    {gerarPaginas().map((pagina, index) => (
                      <button
                        key={index}
                        className={`paginacao-numero ${pagina === paginaAtual ? 'ativo' : ''} ${pagina === '...' ? 'separador' : ''}`}
                        onClick={() => typeof pagina === 'number' && mudarPagina(pagina)}
                        disabled={pagina === '...'}
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    className="paginacao-btn"
                    onClick={() => mudarPagina(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                  >
                    Próxima
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListaComunicados;
